import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';
import { useNavigate } from 'react-router';
import { isSessionExpiredError } from '@traceio-org/auth-ui';
import { isPlatformApiError } from '@traceio-org/platform-client';
import {
  NAVIGATION_DENY_REASONS,
  isProductKey,
  type NavigationDecision,
  type NavigationDenyReason,
  type NavigationRequest,
  type ProductKey,
} from '@traceio-org/platform-contracts';
import { planDecision, type DecisionContext, type DecisionPlan, type HubLocationState, type LoginNotice } from '../navigation/decision';
import { validReturnPath } from '../security/return-path';
import { useBootstrap, useHub } from './context';
import { rememberSigningInProduct } from './signing-in-product';

/** Sets "<title> | Trace" and moves focus to the page heading whenever the page title changes. */
export function usePageHeading(title: string) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    document.title = `${title} | Trace`;
    ref.current?.focus({ preventScroll: true });
  }, [title]);
  return ref;
}

export type LoadState<T> = { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };

/**
 * Runs an async read when its dependencies change and exposes loading, ready and error states plus a
 * retry. A run whose effect was cleaned up never writes state, so StrictMode's double effects and fast
 * navigation cannot show stale data.
 */
export function useLoad<T>(load: (signal: AbortSignal) => Promise<T>, deps: DependencyList): [LoadState<T>, () => void] {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading' });
    load(controller.signal).then(
      (data) => {
        if (active) setState({ status: 'ready', data });
      },
      (error: unknown) => {
        if (active) setState({ status: 'error', error });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
    // The caller's dependency list stands in for `load`, which is a new closure on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return [state, retry];
}

/** True when an error means the person is no longer signed in (expired session or a 401 from the API). */
export function isSessionEnded(error: unknown): boolean {
  return isSessionExpiredError(error) || (isPlatformApiError(error) && error.status === 401);
}

export function productQuery(productKey: ProductKey | null | undefined): string {
  return productKey ? `?product=${productKey}` : '';
}

export function loginRoute(productKey: ProductKey | null | undefined): string {
  return productKey ? `/login/${productKey}` : '/login';
}

export function sessionExpiredPath(productKey: ProductKey | null | undefined): string {
  return `/session-expired${productQuery(productKey)}`;
}

/** Reads a product from a query parameter; anything that is not a product key is ignored. */
export function productFromSearch(search: string): ProductKey | null {
  const value = new URLSearchParams(search).get('product');
  return isProductKey(value) ? value : null;
}

export function denyReasonFromSearch(search: string): NavigationDenyReason | null {
  const value = new URLSearchParams(search).get('reason');
  return (NAVIGATION_DENY_REASONS as readonly string[]).includes(value ?? '') ? (value as NavigationDenyReason) : null;
}

const MAX_CONTEXT_ID_LENGTH = 200;
const REAUTH_REASONS = ['sso_required', 'mfa_required', 'organization_login_required'] as const;

function readNotice(value: unknown): LoginNotice | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Record<string, unknown>;
  const reason = REAUTH_REASONS.find((r) => r === raw.reason);
  if (!reason) return undefined;
  if (raw.kind === 'reauthenticate-loop') return { kind: 'reauthenticate-loop', reason };
  if (raw.kind !== 'reauthenticate') return undefined;
  const hint = typeof raw.connectionHint === 'string' && raw.connectionHint.length <= 128 ? raw.connectionHint : undefined;
  return hint ? { kind: 'reauthenticate', reason, connectionHint: hint } : { kind: 'reauthenticate', reason };
}

/** Router state is untrusted input (history entries survive reloads); every field is re-validated. */
export function readHubState(value: unknown): HubLocationState {
  if (typeof value !== 'object' || value === null) return {};
  const raw = value as Record<string, unknown>;
  const state: HubLocationState = {};
  if (isProductKey(raw.productKey)) state.productKey = raw.productKey;
  const returnPath = validReturnPath(raw.returnPath);
  if (returnPath) state.returnPath = returnPath;
  if (typeof raw.contextId === 'string' && raw.contextId.length > 0 && raw.contextId.length <= MAX_CONTEXT_ID_LENGTH) {
    state.contextId = raw.contextId;
  }
  const notice = readNotice(raw.notice);
  if (notice) state.notice = notice;
  const reauthenticated = REAUTH_REASONS.find((r) => r === raw.reauthenticated);
  if (reauthenticated) state.reauthenticated = reauthenticated;
  if (raw.silentTried === true) state.silentTried = true;
  if (raw.silentFailed === true) state.silentFailed = true;
  if (
    raw.unavailableReason === 'not_deployed' ||
    raw.unavailableReason === 'disabled_in_environment' ||
    raw.unavailableReason === 'destination_refused'
  ) {
    state.unavailableReason = raw.unavailableReason;
  }
  return state;
}

/** Executes a navigation decision: leave for the product, move inside the hub, or restart sign-in. */
export function useFollowDecision() {
  const deps = useHub();
  const { host, registry } = useBootstrap();
  const navigate = useNavigate();
  return useCallback(
    async (decision: NavigationDecision, ctx: Omit<DecisionContext, 'policy'>): Promise<DecisionPlan> => {
      const plan = planDecision(decision, { ...ctx, policy: { registry, host } });
      if (plan.type === 'redirect') {
        deps.assign(plan.url);
      } else if (plan.type === 'navigate') {
        navigate(plan.to, { replace: plan.replace, state: plan.state });
      } else if (deps.sessions.auth0) {
        rememberSigningInProduct(plan.options.productKey);
        await deps.sessions.auth0.beginSignIn(plan.options);
      } else {
        // A sign-in plan only arises for Auth0 sessions; without Auth0 fall back to the login page.
        navigate(loginRoute(plan.options.productKey), { replace: true });
      }
      return plan;
    },
    [deps, host, registry, navigate],
  );
}

/**
 * Asks platform-api where the signed-in person should go and follows the answer. A failure leaves the
 * caller in a recoverable state (`failed`), never on a guessed product list; an ended session goes to
 * the session-expired page once.
 */
export function useDecideAndFollow() {
  const deps = useHub();
  const follow = useFollowDecision();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  const run = useCallback(
    async (
      request: NavigationRequest,
      extra: Pick<DecisionContext, 'reauthenticated' | 'replace'> = {},
    ): Promise<DecisionPlan | null> => {
      setFailed(false);
      const mode = deps.sessions.active()?.mode ?? 'auth0';
      const body: NavigationRequest = {};
      if (request.productKey) body.productKey = request.productKey;
      if (request.returnPath) body.returnPath = request.returnPath;
      if (request.contextId) body.contextId = request.contextId;
      let decision: NavigationDecision;
      try {
        decision = await deps.client.decide(body);
      } catch (error) {
        if (isSessionEnded(error)) {
          navigate(sessionExpiredPath(request.productKey), { replace: true });
        } else {
          console.warn('account-hub: navigation decision failed', error);
          setFailed(true);
        }
        return null;
      }
      try {
        return await follow(decision, { ...body, mode, ...extra });
      } catch (error) {
        console.warn('account-hub: could not follow the navigation decision', error);
        setFailed(true);
        return null;
      }
    },
    [deps, follow, navigate],
  );

  return { run, failed, clearFailure: useCallback(() => setFailed(false), []) };
}

/**
 * "Use a different account": with Auth0, sign in again with prompt=login so another identity can be
 * entered; with development identity, go back to the fixture list.
 */
export function useSwitchAccount(productKey: ProductKey | null | undefined) {
  const deps = useHub();
  const navigate = useNavigate();
  return useCallback(async () => {
    const active = deps.sessions.active();
    if (!deps.sessions.auth0 || active?.mode === 'development') {
      navigate(loginRoute(productKey));
      return;
    }
    await deps.sessions.auth0.beginSignIn({ ...(productKey ? { productKey } : {}), prompt: 'login' });
  }, [deps, navigate, productKey]);
}
